import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Users, Globe, Shield, Tag,
    Layers, User, Calendar, Loader2, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    CheckCircle2, XCircle, X, Fingerprint, FileText,
    Network, ChevronUp, ChevronDown
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import { useClientLevel } from "../../hooks/useClientLevel";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";

interface ADGroup {
    id: number;
    created: string;
    updated: string;
    agent_id: number;
    client_id: number;
    asset_id: number;
    ad_domain_guid: string;
    domain: string;
    category: string;
    scope: string;
    distinguished_name: string;
    sam_account_name: string;
    common_name: string;
    object_sid: string;
    object_guid: string;
    description: string;
    group_type: string;
    name: string;
    managed_by: string;
    is_critical_system_object: boolean;
    empty: boolean;
    cn: string;
    created_when: string;
    modified_when: string;
    group_created: string;
    member: string;
}

const parseJsonArray = (raw: string | string[]): string[] => {
    if (Array.isArray(raw)) {
        return raw;
    }

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const shortName = (dn: string) => {
    const match = dn.match(/^CN=([^,]+)/);
    return match ? match[1] : dn;
};

const Val = ({ v }: { v: string | number | undefined | null }) => {
    const empty = v === undefined || v === null || v === "-" || v === "";
    return (
        <span className={empty ? " text-gray-200" : "text-gray-200"}>
            {empty ? "—" : String(v)}
        </span>
    );
};

// Scope → color mapping
const scopeColor: Record<string, string> = {
    DomainLocal: "bg-blue-500/10 border-blue-500/20 text-blue-300",
    Global: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
    Universal: "bg-purple-500/10 border-purple-500/20 text-purple-300",
};

// ─── Detail Panel ────────────────────────────────────────────────────────────
function GroupDetailPanel({ group, onClose }: { group: ADGroup; onClose: () => void }) {
    const members = parseJsonArray(group.member);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const Section = ({ title, icon, colorClass = "text-indigo-400" }: { title: string; icon: React.ReactNode; colorClass?: string }) => (
        <div className="flex items-center gap-2 mb-3 mt-5 first:mt-0">
            <span className={colorClass}>{icon}</span>
            <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">{title}</span>
            <div className="flex-1 h-px bg-gray-700/50" />
        </div>
    );

    const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
        <div className="flex flex-col gap-0.5">
            <span className="text-[9px] text-gray-500 uppercase tracking-widest">{label}</span>
            <div className="text-[11px]">{children}</div>
        </div>
    );

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-[1px]" onClick={onClose} />

            {/* Panel */}
            <div className="fixed top-0 right-0 h-full w-[30%] min-w-[340px] bg-gray-900 border-l border-gray-700 z-55 flex flex-col shadow-2xl animate-slide-in-right">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/60 bg-gray-800/60 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center">
                            <Users size={16} className="text-indigo-400" />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-white">{group.name}</p>
                            <p className="text-[10px] font-mono text-gray-200">{group.sam_account_name}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-200 hover:text-white hover:bg-gray-700 transition-all">
                        <X size={15} />
                    </button>
                </div>

                {/* Pills row */}
                <div className="flex flex-wrap gap-1.5 px-5 py-3 border-b border-gray-700/40 bg-gray-800/30 shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold border ${scopeColor[group.scope] ?? "bg-gray-500/10 border-gray-500/20 text-gray-300"}`}>
                        <Network size={9} /> {group.scope}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-yellow-500/10 border border-yellow-500/20 text-yellow-300">
                        <Shield size={9} /> {group.category}
                    </span>
                    {group.is_critical_system_object && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-red-500/10 border border-red-500/20 text-red-400">
                            <Shield size={9} /> Critical
                        </span>
                    )}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold border ${group.empty
                        ? "bg-gray-500/10 border-gray-500/20 text-gray-500"
                        : "bg-green-500/10 border-green-500/20 text-green-400"
                        }`}>
                        {group.empty ? <XCircle size={9} /> : <CheckCircle2 size={9} />}
                        {group.empty ? "Empty" : `${members.length} member${members.length !== 1 ? "s" : ""}`}
                    </span>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1 text-gray-200">

                    {/* Identity */}
                    <Section title="Identity" icon={<Users size={12} />} colorClass="text-indigo-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Name"><Val v={group.name} /></Field>
                        <Field label="SAM Account"><span className="font-mono text-gray-300"><Val v={group.sam_account_name} /></span></Field>
                        <Field label="Common Name"><Val v={group.common_name} /></Field>
                        <Field label="Domain"><Val v={group.domain} /></Field>
                        <Field label="Managed By"><Val v={group.managed_by} /></Field>
                        <Field label="CN Path"><span className="font-mono text-[10px] text-gray-200 break-all"><Val v={group.cn} /></span></Field>
                    </div>

                    {/* Description */}
                    {group.description && group.description !== "-" && (
                        <>
                            <Section title="Description" icon={<FileText size={12} />} colorClass="text-blue-400" />
                            <p className="text-[11px] text-gray-200 leading-relaxed">{group.description}</p>
                        </>
                    )}

                    {/* Timestamps */}
                    <Section title="Timestamps" icon={<Calendar size={12} />} colorClass="text-purple-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Created"><span className="font-mono text-[10px] text-gray-300"><Val v={group.created_when} /></span></Field>
                        <Field label="Modified"><span className="font-mono text-[10px] text-gray-300"><Val v={group.modified_when} /></span></Field>
                        <Field label="Group Created"><span className="font-mono text-[10px] text-gray-300"><Val v={group.group_created} /></span></Field>
                    </div>

                    {/* Identifiers */}
                    <Section title="Identifiers" icon={<Fingerprint size={12} />} colorClass="text-rose-400" />
                    <div className="flex flex-col gap-3">
                        <Field label="Object SID">
                            <span className="font-mono text-[10px] text-purple-300/80 break-all"><Val v={group.object_sid} /></span>
                        </Field>
                        <Field label="Object GUID">
                            <span className="font-mono text-[10px] text-gray-200 break-all"><Val v={group.object_guid} /></span>
                        </Field>
                        <Field label="Group Type">
                            <span className="font-mono text-[10px] text-gray-200"><Val v={group.group_type} /></span>
                        </Field>
                        <Field label="Distinguished Name">
                            <span className="font-mono text-[10px] text-gray-200 break-all"><Val v={group.distinguished_name} /></span>
                        </Field>
                    </div>

                    {/* Members */}
                    <Section title={`Members (${members.length})`} icon={<User size={12} />} colorClass="text-cyan-400" />
                    {members.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                            {members.map((m, i) => (
                                <span key={i} className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 rounded text-[9px] font-bold text-indigo-300">
                                    {shortName(m)}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <span className=" text-gray-200 text-[10px]">No members</span>
                    )}

                    <div className="h-6" />
                </div>
            </div>
        </>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function ADGroups({ asset_id }: { asset_id: any }) {
    const [groups, setGroups] = useState<ADGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedGroup, setSelectedGroup] = useState<ADGroup | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    const level = useClientLevel();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchGroups = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;

            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch}%` } },
                    { sam_account_name: { $ilike: `%${debouncedSearch}%` } },
                    { domain: { $ilike: `%${debouncedSearch}%` } },
                    { description: { $ilike: `%${debouncedSearch}%` } },
                    { distinguished_name: { $ilike: `%${debouncedSearch}%` } },
                    { scope: { $ilike: `%${debouncedSearch}%` } },
                    { category: { $ilike: `%${debouncedSearch}%` } },
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/ad_groups", { params });

            if (response.data.status) {
                setGroups(response.data.data);
                setTotalRecords(response.data.total ?? response.data.data.length);
            } else {
                showError("Failed to load AD groups");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    }, [level, debouncedSearch, currentPage, limit, sortConfig, asset_id]);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, icon }: { label: string; sortKey: string; icon?: React.ReactNode }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group select-none"
            >
                <div className="flex items-center gap-2">
                    {icon}
                    {label}
                    <div className="flex items-center ml-auto">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-blue-400" />
                            ) : (
                                <ChevronDown size={12} className="text-blue-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {selectedGroup && (
                <GroupDetailPanel group={selectedGroup} onClose={() => setSelectedGroup(null)} />
            )}

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-indigo-600/20 p-1.5 rounded-lg border border-indigo-500/30">
                        <Users size={16} className="text-indigo-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">AD Groups</h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-indigo-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search groups..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchGroups}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0 disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-indigo-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <SortableHeader label="Group" sortKey="name" icon={<Users size={13} className="text-indigo-400" />} />
                                <SortableHeader label="Domain" sortKey="domain" icon={<Globe size={13} className="text-blue-400" />} />
                                <SortableHeader label="Scope" sortKey="scope" icon={<Network size={13} className="text-emerald-400" />} />
                                <SortableHeader label="Category" sortKey="category" icon={<Shield size={13} className="text-yellow-400" />} />
                                <SortableHeader label="Managed By" sortKey="managed_by" icon={<User size={13} className="text-cyan-400" />} />
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Layers size={13} className="text-pink-400" /> Members</div>
                                </th>
                                <SortableHeader label="Critical" sortKey="is_critical_system_object" icon={<Tag size={13} className="text-red-400" />} />
                                <SortableHeader label="Created" sortKey="created_when" icon={<Calendar size={13} className="text-orange-400" />} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading groups...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : groups.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <Users size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                                                {debouncedSearch ? `No results for "${debouncedSearch}"` : "No groups found"}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                groups.map((group) => {
                                    const members = parseJsonArray(group.member);
                                    return (
                                        <tr
                                            key={group.id}
                                            onClick={() => setSelectedGroup(group)}
                                            className={`hover:bg-gray-800/60 transition-colors cursor-pointer ${selectedGroup?.id === group.id ? "bg-indigo-500/5 border-l-2 border-l-indigo-500" : ""
                                                }`}
                                        >
                                            {/* Group Name */}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-full bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center shrink-0">
                                                        <Users size={11} className="text-indigo-400" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[11px] font-bold text-gray-100">{group.name}</p>
                                                        <p className="text-[9px] font-mono text-gray-500">{group.sam_account_name}</p>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Domain */}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="text-[11px] text-gray-300"><Val v={group.domain} /></span>
                                            </td>

                                            {/* Scope */}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[9px] font-bold ${scopeColor[group.scope] ?? "bg-gray-500/10 border-gray-500/20 text-gray-300"}`}>
                                                    <Network size={9} /> {group.scope}
                                                </span>
                                            </td>

                                            {/* Category */}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 border border-yellow-500/20 rounded text-[9px] font-bold text-yellow-300">
                                                    <Shield size={9} /> {group.category}
                                                </span>
                                            </td>

                                            {/* Managed By */}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <Val v={group.managed_by} />
                                            </td>

                                            {/* Members */}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {members.length > 0 ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/20 rounded text-[9px] font-bold text-cyan-300">
                                                            <Layers size={9} /> {members.length}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/20 rounded text-[9px] font-bold text-cyan-300">
                                                            <Layers size={9} /> 0
                                                        </span>
                                                    </div>
                                                )}
                                            </td>

                                            {/* Critical */}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {group.is_critical_system_object ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-400">
                                                        <CheckCircle2 size={11} /> Yes
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold  text-gray-200">
                                                        <XCircle size={11} /> No
                                                    </span>
                                                )}
                                            </td>

                                            {/* Created */}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="text-[10px] font-mono text-gray-200">{group.created_when || "—"}</span>
                                                    {group.modified_when && group.modified_when !== group.created_when && (
                                                        <span className="text-[9px] font-mono  text-gray-200">mod: {group.modified_when}</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1  text-gray-200">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="text-[9px] text-indigo-400 hover:text-indigo-300 font-bold uppercase tracking-widest transition-colors"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}